import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ActionMenu,
  clampText,
  colors2,
  Container,
  ellipsis,
  icons,
  ModalConfirm,
  ModalSmall,
  SANS_4,
  Section,
  SkeletonLoader,
  UI_BODY_BOLD_SM_DF,
  UI_BODY_SM_DF,
  UI_CAPTION_DF,
  UI_TAG,
  Wrapper1000,
  Wrapper650,
} from "oolib";
import { AIMessage } from "./comps/Messages/AI/index.js";
import { getDefaultAgentVersion } from "./agentVersion";
import { StreamingAIMessage, useStreamedAskAI } from "./comps/Messages/StreamingAI/index.js";
import { SearchBar } from "./comps/SearchBar/index.js";
import { useGetQueryData } from "../../utils/react-query-hooks/general.js";
import { useSavedTplSelection } from "../../utils/customHooks/useSavedTplSelection.js";
import { ChatbotInfo } from "./comps/ChatbotInfo/index.js";
import { ErrorMessage } from "./comps/Messages/Error/index.js";
import { ChatHistory } from "./comps/ChatHistory/index.js";
import {
  useDeleteChatById,
  useGetAllChatsByThisUser,
  useGetChatById,
} from "./hooks/index.js";
import {
  SidebarProvider,
  useSidebarContext,
} from "./contexts/sidebarContext.js";
import { CardAnnoExplorer } from "../analysisModules/AnnotationExplorer/comps/CardAnnoExplorer/index.js";
import { getRoutePathToPublishedContent } from "@okf/ootils";
import { __GetContentTypeConfigNew } from "@/utils/getters/gettersV2";
import { LandingComp } from "./comps/LandingComp/index.js";
import { resolveAskAICopy } from "../analysisModules/TopicClusteringTreeView/PrioritiesLayout/copy";
import { BottomOverlay, ScrollArea } from "./styled.js";
import { useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import { SidebarLayoutV2 } from "../layout/SidebarLayoutV2/index.js";
import { useDynamicLayoutHeight } from "../analysisModules/InsightMatrix/utils/useDynamicLayoutHeight.js";
import { LAYOUT_CONSTANTS } from "@/_self_managed/LAYOUT_CONSTANTS.js";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons/index.js";
import { TabBarStyle2 } from "oolib/dist/v2/components/TabBar/index.js";
import Tooltip from "oolib/dist/v2/components/Tooltip/index.js";
import styled from "styled-components";
import { useAppSettingsContext } from "../../contexts/appSettingsContext.js";
import { FilterPanel } from "./comps/FilterPanel/index.js";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetAiChatSuggestions } from "./hooks/index.js";
import { SuggestionsLanding } from "./comps/SuggestionsLanding/index.js";
import { AnchorPill } from "./comps/AnchorPill/index.js";
import { buildSignalCategoryTheme } from "../analysisModules/comps/SignalFilters/signalCategories.js";
import { getAnnoMode } from "../../utils/getAnnoMode.js";

const StyledChatElemInSidebar = styled.div`
  ${clampText(2)};
  p::first-letter {
    text-transform: uppercase;
  }
`;

const getResponseFromAskAI = async ({ query, chatId, activeLang, immutableActiveFilters, immutableContentTypes, aiChatAgent, anchor }) => {
  return axios.post(`/api/aiChat/getResponse/${chatId}`, {
    ...(chatId === "new" ? {
      immutableActiveFilters: immutableActiveFilters || null,
      immutableContentTypes: immutableContentTypes || null,
    } : {}),
    // Topic anchor rides on every message (see StreamingAI for the SSE twin).
    ...(anchor ? { anchor } : {}),
  }, {
    params: {
      query,
      activeLang,
      // Only send aiChatAgent on a new chat — for existing chats it's locked
      // server-side from the chat doc and the param is ignored anyway.
      ...(chatId === "new" && aiChatAgent ? { aiChatAgent } : {}),
    },
  });
};

/** Syncs sidebar context state → SidebarLayoutV2 toggle state */
const RsbToggleBridge = ({ setSidebarToggleState }) => {
  const { showSidebar } = useSidebarContext();
  useEffect(() => {
    setSidebarToggleState(prev => ({ ...prev, rsb: !!showSidebar }));
  }, [showSidebar]);
  return null;
};

/** Right sidebar content: rendered via SidebarLayoutV2's rsbComp */
const RsbReferences = () => {
  const { showSidebar, setShowSidebar, setContent } = useSidebarContext();
  const { SELF_MANAGED_BASE_CONFIGS } = useGetQueryData("platformConfigs");

  const handleClose = () => {
    setShowSidebar(undefined);
    setContent(null);
  };

  if (!showSidebar) return null;

  const chunkCount = showSidebar?.relatedChunks?.data?.length || 0;
  const docFieldCount = showSidebar?.relatedDocumentFields?.length || 0;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{
        padding: "1rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: `1px solid ${colors2.grey10}`,
      }}>
        <UI_BODY_BOLD_SM_DF>
          All References {chunkCount + docFieldCount ? `(${chunkCount + docFieldCount})` : ""}
        </UI_BODY_BOLD_SM_DF>
        <ButtonGhost icon="X" S onClick={handleClose} />
      </div>
      <ScrollArea style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "1rem", background: colors2.grey2 }}>
        {showSidebar?.relatedChunks?.data?.map((chunk) => (
          <CardAnnoExplorer
            cardColor={colors2.grey2}
            singleAnno={chunk}
            showTagSection={false}
            scrollIntoViewElemIdOverride={chunk.meta?.valuePath}
          />
        ))}
        {showSidebar?.relatedDocumentFields?.map((docField) => {
          const contentTypeDisplay =
            __GetContentTypeConfigNew(docField.contentType)?.general?.content
              ?.title || docField.contentType;
          const href = getRoutePathToPublishedContent({
            contentType: docField.contentType,
            contentId: docField.documentId,
            SELF_MANAGED_BASE_CONFIGS,
            scrollIntoViewElemId: docField.valuePath,
          });
          return (
            <a
              key={`${docField.documentId}_${docField.valuePath}`}
              target="_blank"
              href={href}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div
                style={{
                  backgroundColor: colors2.grey2,
                  borderRadius: "8px",
                  padding: "1rem",
                }}
              >
                <UI_TAG style={{ color: colors2.grey60 }}>
                  {contentTypeDisplay}
                </UI_TAG>
                <div style={{ marginTop: "0.4rem" }}>
                  <UI_BODY_BOLD_SM_DF>{docField.fieldLabel}</UI_BODY_BOLD_SM_DF>
                </div>
                <UI_CAPTION_DF
                  style={{ color: colors2.grey60, marginTop: "0.2rem" }}
                >
                  {docField.documentTitle}
                </UI_CAPTION_DF>
              </div>
            </a>
          );
        })}
      </ScrollArea>
    </div>
  );
};

const Main = ({
  chatHistory,
  chatLoadStatus,
  deleteChat,
  deleteChatStatus,
  handleSendMessage,
  activeChatId,
  setActiveChatId,
  setChatHistory,
  getResponseStatus,
  scrollIntoView,
  handleRetryRequest,
  sidebarCollapsed,
  onToggleSidebar,
  gridHeightCSS,
  streamState,
  onStopStream,
  onRetryStream,
  // Topic-driven extras (split tenants): suggestion cards for the empty
  // state, the chips' send handler, and the anchor pill above the composer.
  suggestions,
  suggestionsStatus,
  showSuggestions,
  signalTheme,
  firstName,
  onPickSuggestion,
  onFollowUp,
  currentAnchor,
  onClearAnchor,
}) => {
  const { showSidebar, setShowSidebar, content, setContent } =
    useSidebarContext();
  const queryClient = useQueryClient();
  const sendBusy = getResponseStatus === "loading" || streamState.active;
  return (
    <div style={{ flex: 1, position: "relative" }}>
      {/* Expand sidebar button — only when collapsed */}
      {sidebarCollapsed && (
        <div style={{ position: "absolute", top: "1rem", left: "1rem", zIndex: 50 }}>
          <Tooltip text="Open sidebar" placement="right">
            <ButtonGhost
              icon="ArrowLineRight"
              S
              onClick={onToggleSidebar}
            />
          </Tooltip>
        </div>
      )}
      <Container>
        <Wrapper650>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: gridHeightCSS,
              gap: "1rem",
            }}
          >
            {/* Bottom-anchored in every state, including the suggestion
                landing: the greeting and cards then sit exactly where the
                first answer will, so sending the first message does not jump
                the page (justify-content cannot be transitioned). */}
            <div style={{ flexGrow: 1, position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <Section
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2rem",
                  padding: "2rem 0",
                }}
              >
                {chatLoadStatus === "loading" ? (
                  <SANS_4>Loading...</SANS_4>
                ) : chatLoadStatus === "error" ? (
                  <SANS_4>Error...</SANS_4>
                ) : chatHistory?.length > 0 ? (
                  <ChatHistory
                    chatHistory={chatHistory}
                    activeChatId={activeChatId}
                    onFollowUp={onFollowUp}
                  />
                ) : showSuggestions ? (
                  <SuggestionsLanding
                    data={suggestions}
                    status={suggestionsStatus}
                    theme={signalTheme}
                    firstName={firstName}
                    copy={resolveAskAICopy(suggestions?.prioritiesCopy, "chat")}
                    onPick={onPickSuggestion}
                    disabled={sendBusy}
                  />
                ) : (
                  <LandingComp />
                )}

                {/* V2 ReAct agent — streamed response (status → trail → live answer) */}
                {streamState.active || streamState.error || streamState.stopped ? (
                  <div ref={scrollIntoView}>
                    <StreamingAIMessage
                      stream={streamState}
                      onStop={onStopStream}
                      onRetry={onRetryStream}
                      onFollowUp={onFollowUp}
                    />
                  </div>
                ) : null}

                {/* V1 legacy agent — blocking request states */}
                {getResponseStatus === "loading" ||
                getResponseStatus === "error" ? (
                  <div ref={scrollIntoView}>
                    {getResponseStatus === "loading" ? (
                      <AIMessage
                        key="loading..."
                        message={"Loading..."}
                        streaming={true}
                        loading={true}
                        activeChatId={activeChatId}
                      />
                    ) : (
                      <ErrorMessage onRetry={handleRetryRequest} />
                    )}
                  </div>
                ) : null}
              </Section>
            </div>

            <div
              style={{
                position: "sticky",
                bottom: "0rem",
                zIndex: "100",
              }}
            >
              <AnchorPill anchor={currentAnchor} onClear={sendBusy ? null : onClearAnchor} />
              <SearchBar
                showDeleteChatButton={chatHistory?.length !== 0}
                deleteChat={() =>
                  deleteChat(
                    { chatId: activeChatId },
                    {
                      onSuccess: () => {
                        setChatHistory([]);
                        setActiveChatId(undefined);
                        setShowSidebar(undefined);
                        setContent(null);
                        queryClient.invalidateQueries(["allChatsByThisUser"]);
                      },
                    }
                  )
                }
                disableOnSubmit={getResponseStatus === "loading" || streamState.active}
                deleteChatStatus={
                  deleteChatStatus === "loading" || deleteChatStatus === "error"
                    ? deleteChatStatus
                    : undefined
                }
                onSubmit={handleSendMessage}
              />
              <div style={{ marginTop: "0.6rem" }}>
                <ChatbotInfo variant="modal" />
              </div>
            </div>
          </div>
        </Wrapper650>
      </Container>
    </div>
  );
};

const SIDEBAR_PAD = 4;

const StyledSidebarChatListButton = styled.button`
  text-align: left;
  width: 100%;
  cursor: pointer;
  padding: 0.9rem;
  border: none;
  border-radius: 6px;
  background: ${({ isActive }) => (isActive ? "#0000001A" : "transparent")};
  &:hover {
    background-color: #0000001A;
  }
  .action-menu-wrapper {
    opacity: 0;
  }
  &:hover .action-menu-wrapper {
    opacity: 1;
  }
`;

const AiChat = () => {
  const userData = useGetQueryData("userData");
  const allTpls = useGetQueryData("allTpls");
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = APP_SETTINGS.lang?.value || "en";
  const { data: allChatsByThisUser, status: allChatsByThisUserLoadingStatus } =
    useGetAllChatsByThisUser(userData.user._id);
  const queryClient = useQueryClient();
  const { mutate: deleteChat, status: deleteChatStatus } = useDeleteChatById();

  const [chatHistory, setChatHistory] = useState([]);
  const [getResponseStatus, setGetResponseStatus] = useState("idel");
  const [retryRequestConfig, setRetryRequestConfig] = useState(); // state to store the request config in case of a failure so that it can retried on a click.

  // Streamed responses (V2 ReAct agent only — the SSE endpoint always runs
  // the ReAct pipeline, so V1 chats stay on the blocking POST path).
  const {
    stream: streamState,
    start: startStream,
    stop: stopStream,
    reset: resetStream,
    retry: retryStream,
  } = useStreamedAskAI();

  /**
   * Deep link into an existing chat: `/ai-chat?chatId=<id>`.
   *
   * Surfaces outside the chat — the dashboard's Ask AI widget — ask their
   * question against a new chat and then offer to continue it here. Without
   * this the page always mounted on a blank chat, so "follow up" silently
   * threw the question and its answer away and started over.
   *
   * Read once into state, then dropped from the URL, so a later "new chat"
   * is not pulled back into the linked one. Because activeChatId is set on
   * the FIRST render, useSavedTplSelection below is disabled from the start
   * and the chat's own persisted filters win, as they do on any other chat.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeChatId, setActiveChatId] = useState(() => searchParams.get("chatId") || undefined);
  useEffect(() => {
    if (!searchParams.get("chatId")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("chatId");
    setSearchParams(next, { replace: true });
    // Mount only: the param has done its job the moment it reaches state.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const requestIdRef = useRef(0); // increments on every chat switch or new request — used to discard stale responses
  const { data: activeChatData, status: chatLoadStatus } = useGetChatById({
    chatId: activeChatId,
    enabled: !!activeChatId,
  });

  // --- Filter state ---
  const publishingTpls = useMemo(
    () => (allTpls || []).filter((tpl) => tpl.general?.segment === "publishing"),
    [allTpls]
  );
  const [selectedTpls, setSelectedTpls] = useState(publishingTpls);
  const [activeFilters, setActiveFilters] = useState([]);
  // Which agent version NEW conversations default to. Shared with the Reports
  // "Ask AI" widget so both surfaces start on the same version.
  const defaultAgent = getDefaultAgentVersion();
  // Locked once a chat has been created.
  const [aiChatAgent, setAiChatAgent] = useState(defaultAgent);
  // Draft state: persisted across chat switches so "new chat" survives
  const [draftNewChatFilters, setDraftNewChatFilters] = useState([]);
  const [draftNewChatSelectedTpls, setDraftNewChatSelectedTpls] = useState(publishingTpls);
  const [draftNewChatAiChatAgent, setDraftNewChatAiChatAgent] = useState(defaultAgent);
  // Filters + agent are locked once a chat has been created (activeChatId exists)
  const isFilterLocked = !!activeChatId;

  // --- Sidebar tab state ---
  const [sidebarTab, setSidebarTab] = useState("chats"); // "chats" | "filters"

  // --- Topic-driven suggestions + anchor (split-mode tenants only) ---
  // The BE decides (GET /api/aiChat/suggestions → enabled:false on legacy
  // tenants); the mode gate here just avoids the request where it cannot apply.
  const platformConfigs = useGetQueryData("platformConfigs");
  const isSplitTenant = getAnnoMode(platformConfigs) === "split";
  const { data: suggestions, status: suggestionsStatus } = useGetAiChatSuggestions({ enabled: isSplitTenant });
  const showSuggestions = isSplitTenant && suggestionsStatus !== "error" && suggestions?.enabled !== false;
  // Same colours the Topics tab uses; per-tenant categories ride on platformConfigs.
  const signalTheme = useMemo(
    () => buildSignalCategoryTheme(platformConfigs?.SIGNAL_CATEGORIES, { prioritiesCopy: suggestions?.prioritiesCopy }),
    [platformConfigs?.SIGNAL_CATEGORIES, suggestions?.prioritiesCopy]
  );
  const firstName = (userData?.user?.name || "").trim().split(" ")[0] || "";
  // The anchor the NEXT message will send: undefined = inherit the chat's
  // (the BE reads it from the last message), an object = use it,
  // { none: true } = clear it. Reset after each send; the BE has persisted it.
  const [anchorOverride, setAnchorOverride] = useState(undefined);
  const chatAnchor = useMemo(() => {
    const last = [...(chatHistory || [])].reverse().find((m) => m?.author === "assistant");
    return last?.metadata?.anchor?.nodeId ? last.metadata.anchor : null;
  }, [chatHistory]);
  const currentAnchor = anchorOverride?.none ? null : anchorOverride || chatAnchor;

  // Reset selectedTpls when publishingTpls changes (e.g. on first load)
  useEffect(() => {
    if (publishingTpls.length > 0 && selectedTpls.length === 0) {
      setSelectedTpls(publishingTpls);
    }
  }, [publishingTpls]);

  // Persist template selection for this session (same convention as saved
  // filters). Only while no chat is open — an open chat's tpls come from and
  // stay locked to the chat doc. Must sit BELOW the reset effect above so a
  // restored selection lands after (i.e. wins over) the reset's default.
  useSavedTplSelection({
    module: "aiChat",
    optionIds: publishingTpls.map((t) => t.kp_content_type),
    selectedIds: selectedTpls.map((t) => t.kp_content_type),
    restoreSelection: (ids) => {
      const tpls = publishingTpls.filter((t) => ids.includes(t.kp_content_type));
      setSelectedTpls(tpls);
      setDraftNewChatSelectedTpls(tpls);
    },
    enabled: !activeChatId,
  });

  useEffect(() => {
    if (chatLoadStatus === "success" && !!activeChatData) {
      setChatHistory(activeChatData.messages);
      // Load this chat's persisted filters
      if (activeChatData.immutableActiveFilters) {
        setActiveFilters(activeChatData.immutableActiveFilters);
      } else {
        setActiveFilters([]);
      }
      if (activeChatData.immutableContentTypes) {
        const matchedTpls = publishingTpls.filter(tpl =>
          activeChatData.immutableContentTypes.includes(tpl.kp_content_type)
        );
        setSelectedTpls(matchedTpls.length > 0 ? matchedTpls : publishingTpls);
      } else {
        setSelectedTpls(publishingTpls);
      }
      // Restore the locked agent version from the chat doc
      if (activeChatData.aiChatAgent) {
        setAiChatAgent(activeChatData.aiChatAgent);
      } else {
        // Pre-existing chats without aiChatAgent field default to legacy v1
        setAiChatAgent("v1_singleMultiIntent");
      }
    }
  }, [chatLoadStatus, activeChatData]);

  // Swap the finished stream bubble for the stored message: once the
  // refetched chat actually CONTAINS the streamed answer, clear the stream.
  // Both state updates land in one render, so there is no duplicate flash.
  useEffect(() => {
    if (!streamState.done || !streamState.complete) return;
    const storedAnswerArrived = (chatHistory || []).some(
      (m) =>
        m?.author === "assistant" &&
        m?.content?.value === streamState.complete.answer
    );
    if (storedAnswerArrived) resetStream();
  }, [chatHistory, streamState.done, streamState.complete, resetStream]);

  const handleRetryRequest = async () => {
    setGetResponseStatus("loading");

    if (!retryRequestConfig) {
      throw new Error("retry request config missing");
    }

    const thisRequestId = requestIdRef.current;

    try {
      const response = await axios(retryRequestConfig);
      if (thisRequestId !== requestIdRef.current) return;
      setGetResponseStatus("idel");
      setChatHistory((prev) => [...prev, response.data.finalMessage]);
      setActiveChatId((prev) => (!prev ? response.data.chatId : prev));
    } catch (error) {
      if (thisRequestId !== requestIdRef.current) return;
      setGetResponseStatus("error");
    }
  };

  /**
   * V2 streaming send. The user message lands immediately; the assistant
   * bubble streams status → investigation trail → answer. On complete the
   * streamed text is appended as a plain message so the UI never blanks,
   * then the chat refetch swaps in the stored message (references, charts,
   * feedback actions).
   */
  const handleSendMessageStreaming = ({ value, anchor, callback }) => {
    setChatHistory((prev) => [
      ...prev,
      { author: "user", content: { type: "text", value } },
    ]);

    const thisRequestId = requestIdRef.current;
    const anchorToSend = anchor ?? anchorOverride ?? undefined;
    setAnchorOverride(undefined);

    startStream({
      query: value,
      chatId: activeChatId || "new",
      activeLang,
      immutableActiveFilters: activeFilters.length > 0 ? activeFilters : null,
      // An anchored first message takes its scope from the TOPIC, so it must
      // not also lock content types: the anchor's annotation ids already span
      // whatever datasets the topic covers, and narrowing further would make
      // the answer disagree with the Topics card the user clicked from. This
      // also removes the deep link's race with the saved-template restore,
      // which could otherwise lock a new chat to a pre-restore selection.
      immutableContentTypes:
        !anchorToSend?.nodeId && selectedTpls.length > 0 && selectedTpls.length < publishingTpls.length
          ? selectedTpls.map((d) => d.kp_content_type)
          : null,
      aiChatAgent,
      anchor: anchorToSend,
      onComplete: (evt) => {
        if (thisRequestId !== requestIdRef.current) return;
        // Do NOT append a local copy of the answer — the streaming bubble
        // already shows it in full. It stays on screen until the refetched
        // chat contains the stored message (references, actions, charts);
        // an effect below swaps them in the same render, so the answer
        // never appears twice and never flashes.
        if (!activeChatId) {
          queryClient.invalidateQueries(["allChatsByThisUser"]);
          setDraftNewChatFilters([]);
          setDraftNewChatSelectedTpls(publishingTpls);
          setDraftNewChatAiChatAgent(defaultAgent);
        } else {
          queryClient.invalidateQueries(["getChatById", evt.chatId]);
        }
        setActiveChatId((prev) => (!prev ? evt.chatId : prev));
        callback && callback();
      },
      onError: () => {
        // Error state renders inside the streaming bubble with a retry link;
        // nothing else to do unless the user switched chats mid-flight.
        if (thisRequestId !== requestIdRef.current) resetStream();
      },
    });
  };

  /** User pressed Stop: keep the partial answer, then pick up the saved
   * answer once the backend finishes writing it. */
  const handleStopStream = () => {
    stopStream();
    const chatIdAtStop = activeChatId;
    setTimeout(() => {
      resetStream();
      if (chatIdAtStop) queryClient.invalidateQueries(["getChatById", chatIdAtStop]);
      queryClient.invalidateQueries(["allChatsByThisUser"]);
    }, 8000);
  };

  /**
   * `anchor` (optional): a Topics-tab node to pin this question to —
   * { nodeId, lens, kind, … } from a suggestion card or a follow-up chip, or
   * { none: true } to clear the chat's anchor. Omitted → the pending
   * override, else the chat's existing anchor (the BE inherits it).
   */
  const handleSendMessage = async ({ value, anchor, callback }) => {
    if (aiChatAgent === "v2_reactAgent") {
      return handleSendMessageStreaming({ value, anchor, callback });
    }

    setChatHistory((prev) => [
      ...prev,
      { author: "user", content: { type: "text", value } },
    ]);

    setGetResponseStatus("loading");

    const thisRequestId = requestIdRef.current;
    const anchorToSend = anchor ?? anchorOverride ?? undefined;
    setAnchorOverride(undefined);

    let response;

    try {
      response = await getResponseFromAskAI({
        query: value,
        chatId: activeChatId || "new",
        activeLang,
        immutableActiveFilters: activeFilters.length > 0 ? activeFilters : null,
        // Anchored sends carry no content-type lock — see the streaming path.
        immutableContentTypes: !anchorToSend?.nodeId && selectedTpls.length > 0 && selectedTpls.length < publishingTpls.length
          ? selectedTpls.map((d) => d.kp_content_type)
          : null,
        aiChatAgent,
        anchor: anchorToSend,
      });

      // If user switched chats while this was in-flight, discard the UI update.
      // The backend has already saved the response to the correct chat.
      if (thisRequestId !== requestIdRef.current) return;

      setGetResponseStatus("idel");
      setChatHistory((prev) => [...prev, response.data.finalMessage]);
      if (!activeChatId) {
        queryClient.invalidateQueries(["allChatsByThisUser"]);
        // Chat just created from new-chat mode → clear draft so next "new chat" starts fresh
        setDraftNewChatFilters([]);
        setDraftNewChatSelectedTpls(publishingTpls);
        setDraftNewChatAiChatAgent(defaultAgent);
      } else {
        //because if activeChatId doesnt exist in the first place, this particular cache wont exist only.
        queryClient.invalidateQueries(["getChatById", response.data.chatId]);
      }
      setActiveChatId((prev) => (!prev ? response.data.chatId : prev));
      callback && callback();
    } catch (error) {
      // If user switched chats while this request was in-flight, don't show
      // an error on the new chat. The failed request belongs to the old chat context.
      if (thisRequestId !== requestIdRef.current) return;
      setRetryRequestConfig(error.config);
      setGetResponseStatus("error");
    }
  };

  const scrollIntoView = (node) => {
    if (node) {
      node.scrollIntoView({ behavior: "smooth" });
    }
  };

  // A suggestion card / follow-up chip: send its text pinned to its anchor.
  // The composer cannot be prefilled, so the click sends directly.
  const handlePickSuggestion = (card) => {
    if (!card?.text) return;
    handleSendMessage({ value: card.text, anchor: card.anchor || undefined });
  };
  const handleClearAnchor = () => setAnchorOverride({ none: true });

  // Deep link from the Topics tab: /ai-chat?anchor=<nodeId>&lens=&kind=&title=
  // With no chat open, ask that topic's suggested question straight away
  // (the card's rephrased text when the suggestions carry one, else a plain
  // template), then drop the params so a refresh does not re-ask.
  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkFiredRef = useRef(false);
  useEffect(() => {
    if (deepLinkFiredRef.current) return;
    const params = new URLSearchParams(location.search);
    const nodeId = params.get("anchor");
    if (!nodeId || activeChatId) return;
    // Do NOT wait for the suggestions request: on a large tenant its first
    // call builds the snapshot and rewords every card, so waiting left the
    // chat sitting on its landing for tens of seconds after a click and read
    // as broken. The card's rephrased wording is a nicety; the template says
    // the same thing. Once the request is cached (staleTime Infinity, and the
    // server caches per tree version) the good wording is there anyway.
    const lens = params.get("lens") || undefined;
    const kind = params.get("kind") || "overview";
    const title = params.get("title") || undefined;
    const card =
      (suggestions?.suggestions || []).find((s) => s.nodeId === nodeId && s.kind === kind) ||
      (suggestions?.suggestions || []).find((s) => s.nodeId === nodeId);
    const topicTitle = card?.topicTitle || title || "this topic";
    const text = card?.text || `What are customers saying about ${topicTitle}?`;
    deepLinkFiredRef.current = true;
    // Ask first, then drop the params: the question is what the click was for,
    // and clearing the search only stops a refresh from re-asking.
    handleSendMessage({ value: text, anchor: card?.anchor || { nodeId, lens, kind, title: topicTitle } });
    navigate({ pathname: location.pathname, search: "" }, { replace: true });
  }, [location.search, activeChatId, suggestions]);

  const { parentWrapperRef, gridHeightCSS } = useDynamicLayoutHeight();
  const [sidebarToggleState, setSidebarToggleState] = useState({ lsb: true, rsb: false });
  const [showDeleteChatInSidebarModal, setShowDeleteChatInSidebarModal] =
    useState(undefined);

  const handleStartNewChat = () => {
    requestIdRef.current++; // invalidate any in-flight request
    resetStream(); // silently abort any in-flight stream
    setChatHistory([]);
    setActiveChatId(undefined);
    setAnchorOverride(undefined);
    // Restore draft state for new chat
    setSelectedTpls(draftNewChatSelectedTpls);
    setActiveFilters(draftNewChatFilters);
    setAiChatAgent(draftNewChatAiChatAgent);
    setSidebarTab("chats");
  };

  return (
    <SidebarProvider>
      <RsbToggleBridge setSidebarToggleState={setSidebarToggleState} />
      {showDeleteChatInSidebarModal && (
        <ModalConfirm
          onClose={() => setShowDeleteChatInSidebarModal(false)}
          onCloseText="Cancel"
          onConfirmText="Delete Chat"
          title="Are you sure you want to permanently delete your chat?"
          onConfirm={() => {
            deleteChat(
              { chatId: showDeleteChatInSidebarModal },
              {
                onSuccess: () => {
                  if (activeChatId === showDeleteChatInSidebarModal) {
                    setChatHistory([]);
                    setActiveChatId(undefined);
                  }
                  setShowDeleteChatInSidebarModal(undefined)
                  queryClient.invalidateQueries(["allChatsByThisUser"]);
                },
                onError: () => setShowDeleteChatInSidebarModal(undefined)
              }
            );
          }}
        />
      )}
      <SidebarLayoutV2
        gridHeightCSS={gridHeightCSS}
        contentScrollbarStyling
        lsbComp={
          <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Header: tabs + collapse */}
            <div
              style={{
                padding: `6px ${SIDEBAR_PAD}px`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${colors2.grey10}`,
              }}
            >
              <TabBarStyle2
                S
                saveValueAsString
                options={[
                  { display: "Chats", value: "chats" },
                  { display: "Filters", value: "filters", icon: isFilterLocked ? "LockSimple" : undefined, message: activeFilters.length || undefined },
                ]}
                value={sidebarTab}
                onChange={(id, v) => setSidebarTab(v)}
              />
              <Tooltip text="Collapse sidebar" placement="bottom">
                <ButtonGhost
                  icon="ArrowLineLeft"
                  S
                  onClick={() => setSidebarToggleState(prev => ({...prev, lsb: false}))}
                />
              </Tooltip>
            </div>

            {/* Tab content */}
            <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              {sidebarTab === "chats" ? (
                <>
                  {/* Start New Chat */}
                  <div style={{ padding: `${SIDEBAR_PAD}px`, paddingBottom: 0 }}>
                    <ButtonGhost
                      onClick={handleStartNewChat}
                      icon="PencilLine"
                      S
                      style={{
                        width: "100%",
                        justifyContent: "flex-start",
                        ...(!activeChatId ? { background: colors2.grey10, borderRadius: "6px" } : {}),
                      }}
                    >
                      Start A New Chat
                    </ButtonGhost>
                  </div>
                  {/* Chat list */}
                  <ScrollArea>
                    {allChatsByThisUserLoadingStatus === "loading" ? (
                      [1, 2, 3].map((d) => (
                        <div
                          key={d}
                          style={{
                            padding: `${SIDEBAR_PAD}px`,
                            borderBottom: `1px solid ${colors2.grey10}`,
                          }}
                        >
                          <SkeletonLoader
                            style={{ width: "100%", height: "16px" }}
                          />
                        </div>
                      ))
                    ) : allChatsByThisUserLoadingStatus === "error" ? (
                      <div style={{ padding: `${SIDEBAR_PAD}px` }}>Error Loading Chats</div>
                    ) : (
                      <div
                        style={{
                          padding: "4px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {allChatsByThisUser.map((d) => (
                          <StyledSidebarChatListButton
                            key={d._id}
                            isActive={d._id === activeChatId}
                            onClick={() => {
                              requestIdRef.current++; // invalidate any in-flight request
                              resetStream(); // silently abort any in-flight stream
                              setAnchorOverride(undefined); // the opened chat's own anchor applies
                              // Save current new-chat state to draft if we're leaving new-chat mode
                              if (!activeChatId) {
                                setDraftNewChatFilters(activeFilters);
                                setDraftNewChatSelectedTpls(selectedTpls);
                                setDraftNewChatAiChatAgent(aiChatAgent);
                              }
                              setActiveChatId(d._id);
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                alignItems: "center",
                              }}
                            >
                              <StyledChatElemInSidebar style={{ flex: 1 }}>
                                {/* An anchored chat is named by its topic; the
                                    first question becomes the caption. */}
                                {(() => {
                                  const first = d.messages?.[0]?.content?.value;
                                  const topic = d.messages?.find((m) => m?.author === "assistant")?.metadata?.anchor?.title;
                                  return topic ? (
                                    <>
                                      <UI_BODY_SM_DF>{topic}</UI_BODY_SM_DF>
                                      <UI_CAPTION_DF style={{ color: colors2.grey60 }}>{first}</UI_CAPTION_DF>
                                    </>
                                  ) : (
                                    <UI_BODY_SM_DF>{first}</UI_BODY_SM_DF>
                                  );
                                })()}
                              </StyledChatElemInSidebar>
                              <div className="action-menu-wrapper">
                                <ActionMenu
                                  actions={[
                                    {
                                      disabled: deleteChatStatus === "loading",
                                      display: "Delete Chat",
                                      onClick: () => {
                                        setShowDeleteChatInSidebarModal(d._id);
                                      },
                                    },
                                  ]}
                                />
                              </div>
                            </div>
                          </StyledSidebarChatListButton>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </>
              ) : (
                /* Filters tab */
                <FilterPanel
                  isLocked={isFilterLocked}
                  selectedTpls={selectedTpls}
                  setSelectedTpls={setSelectedTpls}
                  activeFilters={activeFilters}
                  setActiveFilters={setActiveFilters}
                  aiChatAgent={aiChatAgent}
                  setAiChatAgent={setAiChatAgent}
                />
              )}
            </div>
          </div>
        }
        rsbComp={<RsbReferences />}
        rsbWidth={350}
        contentSectionMinWidth="660px"
        toggleState={sidebarToggleState}
        setToggleState={setSidebarToggleState}
        toggleAnimationStyle="slide"
      >
        <div style={{position: 'relative', height: '100%', display: 'flex'}} ref={parentWrapperRef}>
          <BottomOverlay />
          <Main
            chatHistory={chatHistory}
            chatLoadStatus={chatLoadStatus}
            setActiveChatId={setActiveChatId}
            activeChatId={activeChatId}
            deleteChat={deleteChat}
            deleteChatStatus={deleteChatStatus}
            handleSendMessage={handleSendMessage}
            setChatHistory={setChatHistory}
            getResponseStatus={getResponseStatus}
            scrollIntoView={scrollIntoView}
            handleRetryRequest={handleRetryRequest}
            sidebarCollapsed={!sidebarToggleState.lsb}
            onToggleSidebar={() => setSidebarToggleState(prev => ({...prev, lsb: true}))}
            gridHeightCSS={gridHeightCSS}
            streamState={streamState}
            onStopStream={handleStopStream}
            onRetryStream={retryStream}
            suggestions={suggestions}
            suggestionsStatus={suggestionsStatus}
            showSuggestions={showSuggestions}
            signalTheme={signalTheme}
            firstName={firstName}
            onPickSuggestion={handlePickSuggestion}
            onFollowUp={handlePickSuggestion}
            currentAnchor={currentAnchor}
            onClearAnchor={handleClearAnchor}
          />
        </div>
      </SidebarLayoutV2>
    </SidebarProvider>
  );
};
export default AiChat;
